import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { DirectedStory } from 'fym-shared/src/music24/directedVideoApi';
import type { MusicVideoSongMapSection } from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { castDescriptionMessages } from '../messages/castDescriptionMessages';
import { storyMessages as messages } from '../messages/storyMessages';
import { useDirectedCastNames } from '../useDirectedCastNames';
import { beatSectionOf } from './director/choiceRules';
import { SECTION_DOT } from './director/sectionDots';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the director steps; each part uses its own slice of it */
import styles from './DirectorSteps.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StoryCardProps {
  story: DirectedStory;
  number: number;
  sections: readonly MusicVideoSongMapSection[];
  isOn: boolean;
  /** Another card is open for edit (design c3): this one is dimmed and shows no beats. */
  isDim: boolean;
  isEditing: boolean;
  isEdited: boolean;
  disabled: boolean;
  onPick: () => void;
  onEdit: () => void;
  onChange: (story: DirectedStory) => void;
  onDone: () => void;
  onUndo: () => void;
}

/**
 * One story idea (design c1, c3, j2): the title, the line, the beats by section, Pick and Edit. In
 * edit mode every text is a field; the beats keep their sections. The edit stays on this page
 * until "Use this story".
 */
const StoryCardComponent: React.FC<StoryCardProps> = ({
  story,
  number,
  sections,
  isOn,
  isDim,
  isEditing,
  isEdited,
  disabled,
  onPick,
  onEdit,
  onChange,
  onDone,
  onUndo,
}) => {
  const intl = useIntl();
  // Every text shows the cast by name, never by id (owner report 2026-10-18).
  const { named, toIds } = useDirectedCastNames();
  // The recurring people of the story who need a cast picture (fix 2026-10-18).
  const needsPictures = useMemo(
    () => (story.characters ?? []).filter((character) => character.needsPicture).map((character) => character.name),
    [story.characters]
  );
  const head = (
    <div className={styles.optionHead}>
      <span className={styles.optionNumber}>{intl.formatMessage(messages.idea, { number })}</span>
      {isEdited && <span className={styles.edited}>{intl.formatMessage(messages.edited)}</span>}
      <button
        type="button"
        role="radio"
        aria-checked={isOn}
        aria-label={intl.formatMessage(messages.idea, { number })}
        className={clsx(styles.radio, isOn && styles.radioOn)}
        disabled={disabled || isEditing}
        onClick={onPick}
      />
    </div>
  );

  if (isEditing) {
    return (
      <div className={clsx(styles.option, styles.optionOn)} data-testid={`story-${story.id}`}>
        {head}
        <label className={styles.fieldLabel} htmlFor={`story-title-${story.id}`}>
          {intl.formatMessage(messages.fieldTitle)}
        </label>
        <input
          id={`story-title-${story.id}`}
          className={clsx(styles.input, styles.inputTitle)}
          value={named(story.title)}
          onChange={(event) => onChange({ ...story, title: toIds(event.target.value) })}
        />
        <label className={styles.fieldLabel} htmlFor={`story-line-${story.id}`}>
          {intl.formatMessage(messages.fieldLogline)}
        </label>
        <textarea
          id={`story-line-${story.id}`}
          className={styles.input}
          rows={3}
          value={named(story.logline)}
          onChange={(event) => onChange({ ...story, logline: toIds(event.target.value) })}
        />
        <span className={styles.fieldLabel}>{intl.formatMessage(messages.fieldBeats)}</span>
        <div className={styles.beatsEdit}>
          {story.beats.map((beat, index) => {
            const section = beatSectionOf(sections, beat.sectionIndex);
            return (
              // eslint-disable-next-line react/no-array-index-key -- a beat has no id; the order never changes
              <div key={index} className={styles.beat}>
                <span className={styles.beatSection}>
                  <i className={clsx(styles.dot, SECTION_DOT[section.tone])} aria-hidden />
                  {section.name}
                </span>
                <textarea
                  className={styles.input}
                  rows={2}
                  aria-label={section.name}
                  value={named(beat.text)}
                  onChange={(event) =>
                    onChange({
                      ...story,
                      beats: story.beats.map((item, at) =>
                        at === index ? { ...item, text: toIds(event.target.value) } : item
                      ),
                    })
                  }
                />
              </div>
            );
          })}
        </div>
        <div className={styles.optionActions}>
          <SMLButton variant="primary" className={styles.small} onClick={onDone}>
            {intl.formatMessage(messages.done)}
          </SMLButton>
          <SMLButton className={clsx(styles.small, styles.ghost)} onClick={onUndo}>
            {intl.formatMessage(messages.undo)}
          </SMLButton>
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(styles.option, isOn && styles.optionOn, isDim && styles.optionDim)}
      data-testid={`story-${story.id}`}
    >
      {head}
      <div className={styles.optionTitle}>{named(story.title)}</div>
      <p className={styles.optionText}>{named(story.logline)}</p>
      {needsPictures.length > 0 && (
        <p className={styles.optionText} data-testid="story-needs-pictures">
          {intl.formatMessage(castDescriptionMessages.storyNeedsPictures, { names: needsPictures.join(', ') })}
        </p>
      )}
      {!isDim && story.beats.length > 0 && (
        <div className={styles.beats}>
          {story.beats.map((beat, index) => {
            const section = beatSectionOf(sections, beat.sectionIndex);
            return (
              // eslint-disable-next-line react/no-array-index-key -- a beat has no id; the order never changes
              <div key={index} className={styles.beat}>
                <span className={styles.beatSection}>
                  <i className={clsx(styles.dot, SECTION_DOT[section.tone])} aria-hidden />
                  {section.name}
                </span>
                <span>{named(beat.text)}</span>
              </div>
            );
          })}
        </div>
      )}
      {!isDim && (
        <>
          <button
            type="button"
            className={clsx(styles.linkButton, styles.phoneLink)}
            disabled={disabled}
            onClick={onEdit}
          >
            {intl.formatMessage(messages.beatsLink, { count: story.beats.length })}
          </button>
          <div className={clsx(styles.optionActions, styles.desktopOnly)}>
            <SMLButton className={styles.small} disabled={disabled} onClick={onPick}>
              {intl.formatMessage(isOn ? messages.picked : messages.pick)}
            </SMLButton>
            <SMLButton className={clsx(styles.small, styles.ghost)} disabled={disabled} onClick={onEdit}>
              {intl.formatMessage(messages.edit)}
            </SMLButton>
          </div>
        </>
      )}
    </div>
  );
};

export const StoryCard = TypedMemo(StoryCardComponent);
